
import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';

// Define the base condition interface
export interface WorkflowCondition {
  id: string;
  workflow_template: string;
  field_name: string;
  field_label: string;
  operator: string;
  operator_name?: string;
  value: string | number | boolean;
  field_value: string | number | boolean | string[] | null; // Array for IN / NOT_IN operators
  display_value?: string | string[]; // Array when field_value is a list
  key?: string;
  condition_type: 'dropdown' | 'multi_dropdown' | 'text' | 'number' | 'date';
  order: number;
  created_at: string;
  updated_at: string;
}

// Specific condition types
export interface DropdownCondition extends WorkflowCondition {
  condition_type: 'dropdown';
  value: string; // Selected dropdown option (single for equals/not_equals, unused for in/not_in)
  field_value: string | string[] | null; // string for equals/not_equals; string[] for in/not_in
}

export interface MultiDropdownCondition extends WorkflowCondition {
  condition_type: 'multi_dropdown';
  field_value: string[] | null; // Always an array
}

export interface TextCondition extends WorkflowCondition {
  condition_type: 'text';
  value: string; // Text value
}

export interface NumericCondition extends WorkflowCondition {
  condition_type: 'number';
  value: number; // Numeric value
}

export interface DateCondition extends WorkflowCondition {
  condition_type: 'date';
  value: string; // ISO date string
}

// Dropdown value interface
export interface DropdownValue {
  id: string;
  name: string;
}

// Field metadata interface
export interface FieldMetadata {
  name: string;
  label: string;
  type: 'dropdown' | 'multi_dropdown' | 'text' | 'number' | 'date';
  operators: string[];
  operator_name?: string;
}

// Pagination interface for conditions
export interface ConditionPaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: WorkflowCondition[];
}

// Define types for request and response
export interface ConditionCreateInput {
  workflow_template: string;
  field_name: string;
  operator: string;
  value: string | number | boolean;
  field_value: string | number | boolean | string[] | null;
  condition_type: 'dropdown' | 'multi_dropdown' | 'text' | 'number' | 'date';
  order?: number;
}

export interface ConditionUpdateInput extends Partial<ConditionCreateInput> {
  // Additional fields specific to updates can be added here
  update_reason?: string;
}

export interface ConditionEvaluateInput {
  conditions: WorkflowCondition[];
  test_values: Record<string, string | number | boolean>;
}

export interface ConditionEvaluateResult {
  result: boolean;
  condition_results: {
    condition_id: string;
    passed: boolean;
    message?: string;
  }[];
}

// Create API
export const workflowConditionApi = createApi({
  reducerPath: 'workflowConditionApi',
  baseQuery: createExtendedTokenBaseQuery('workflows/'),
  tagTypes: ['WorkflowCondition', 'FieldMetadata', 'DropdownValues', 'FieldOperators'],
  endpoints: (builder) => ({
    // Get conditions for a workflow template
    getWorkflowConditions: builder.query<ConditionPaginatedResponse, string>({
      query: (workflowTemplateId) => ({
        url: 'conditions/',
        params: {
          workflow_template_id: workflowTemplateId,
        },
      }),
      providesTags: (result, error, workflowTemplateId) => [
        { type: 'WorkflowCondition', id: workflowTemplateId },
        'WorkflowCondition'
      ],
    }),
    
    // Get condition by ID
    getConditionById: builder.query<WorkflowCondition, string>({
      query: (id) => `conditions/${id}/`,
      providesTags: (result, error, id) => [{ type: 'WorkflowCondition', id }],
    }),
    
    // Create condition
    createCondition: builder.mutation<WorkflowCondition, ConditionCreateInput>({
      query: (condition) => ({
        url: 'conditions/',
        method: 'POST',
        body: condition,
      }),
      invalidatesTags: (result, error, { workflow_template }) => [
        { type: 'WorkflowCondition', id: workflow_template },
        'WorkflowCondition'
      ],
    }),
    
    // Update condition
    updateCondition: builder.mutation<WorkflowCondition, { id: string; condition: ConditionUpdateInput }>({
      query: ({ id, condition }) => ({
        url: `conditions/${id}/`,
        method: 'PUT',
        body: condition,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'WorkflowCondition', id },
        'WorkflowCondition'
      ],
    }),
    
    // Delete condition
    deleteCondition: builder.mutation<void, string>({
      query: (id) => ({
        url: `conditions/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['WorkflowCondition'],
    }),
    
    // Reorder conditions
    reorderConditions: builder.mutation<void, { workflowTemplateId: string; conditionIds: string[] }>({
      query: ({ workflowTemplateId, conditionIds }) => ({
        url: `workflow-templates/${workflowTemplateId}/conditions/reorder/`,
        method: 'PATCH',
        body: { condition_ids: conditionIds },
      }),
      invalidatesTags: (result, error, { workflowTemplateId }) => [
        { type: 'WorkflowCondition', id: workflowTemplateId },
        'WorkflowCondition'
      ],
    }),
    
    // Evaluate conditions
    evaluateConditions: builder.mutation<ConditionEvaluateResult, ConditionEvaluateInput>({
      query: (data) => ({
        url: 'conditions/evaluate/',
        method: 'POST',
        body: data,
      }),
    }),
    
    // Get all field metadata for an entity type
    getFieldMetadata: builder.query<FieldMetadata[], { entity_type?: string; event_type?: string }>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params.entity_type) queryParams.append('entity_type', params.entity_type);
        if (params.event_type) queryParams.append('event_type', params.event_type);
        
        return {
          url: 'fields/',
          params: queryParams
        };
      },
      providesTags: ['FieldMetadata'],
    }),
    
    // Get dropdown fields for an entity type
    getDropdownFields: builder.query<{ name: string; label: string }[], { entity_type: string; event_type: string }>({
      query: ({entity_type, event_type}) => {
        if(!entity_type || !event_type) return { url: 'dropdown-fields/custom/'};
        return {
          url: `dropdown-fields/${entity_type}/${event_type}/`,
        };
      },
      providesTags: ['FieldMetadata'],
    }),
    
    // Get text fields for an entity type
    getTextFields: builder.query<{ name: string; label: string }[], { entity_type: string; event_type: string }>({
      query: ({entity_type, event_type}) => {
        if(!entity_type || !event_type) return { url: 'text-fields/custom/'};
        return {
          url: `text-fields/${entity_type}/${event_type}/`,
        };
      },
      providesTags: ['FieldMetadata'],
    }),
    
    // Get numeric fields for an entity type
    getNumericFields: builder.query<{ name: string; label: string }[], { entity_type: string; event_type: string }>({
      query: ({entity_type, event_type}) => {
        if(!entity_type || !event_type) return { url: 'numeric-fields/custom/'};
        return {
          url: `numeric-fields/${entity_type}/${event_type}/`,
        };
      },
      providesTags: ['FieldMetadata'],
    }),
    
    // Get date fields for an entity type
    getDateFields: builder.query<{ name: string; label: string }[], { entity_type: string; event_type: string }>({
      query: ({entity_type, event_type}) => {
        if(!entity_type || !event_type) return { url: 'date-fields/custom/'};
        return {
          url: `date-fields/${entity_type}/${event_type}/`,
        };
      },
      providesTags: ['FieldMetadata'],
    }),
    
    // Get dropdown values for a field
    getDropdownValues: builder.query<DropdownValue[], { entity_type: string; fieldName: string }>({
      query: ({entity_type, fieldName}) => {
        if (!entity_type || !fieldName) return { url: 'dropdown-values/custom/status/' };
        return { url: `dropdown-values/${entity_type}/${fieldName}/` };
      },
      providesTags: (result, error, { entity_type, fieldName }) => [
        { type: 'DropdownValues', id: `${entity_type}-${fieldName}` },
      ],
    }),
    
    // Get valid operators for a field
    getFieldOperators: builder.query<{ value: string; display_name?: string; label?: string }[], { entity_type: string; event_type: string; fieldName: string }>({
      query: ({ entity_type, event_type, fieldName }) => {
        if (!entity_type || !event_type || !fieldName) {
          return { url: 'field-operators/custom/status/' };
        }
        return { url: `field-operators/${entity_type}/${event_type}/${fieldName}/` };
      },
      providesTags: (result, error, { fieldName }) => [{ type: 'FieldOperators', id: fieldName }],
    }),
  }),
});

// Export hooks
export const {
  useGetWorkflowConditionsQuery,
  useGetConditionByIdQuery,
  useCreateConditionMutation,
  useUpdateConditionMutation,
  useDeleteConditionMutation,
  useReorderConditionsMutation,
  useEvaluateConditionsMutation,
  useGetFieldMetadataQuery,
  useGetDropdownFieldsQuery,
  useGetTextFieldsQuery,
  useGetNumericFieldsQuery,
  useGetDateFieldsQuery,
  useGetDropdownValuesQuery,
  useGetFieldOperatorsQuery,
} = workflowConditionApi;
